import { handleActions } from 'redux-actions';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import {
  getNextFreelancerOnboardingStep,
  getPrevFreelancerOnboardingStep,
} from '../../../shared/helpers';

export const initialState = {
  currentStep: 'edit_profile',
  isFetching: false,
  isNextEnabled: true,
  freelancer_onboarding: {
    freelancerSteps: [
      {
        key: 'edit_profile',
        name: 'Edit Profile',
        validationProperties: ['name', 'email'],
        status: 'empty',
        type: 'single',
        hero: 'Add some basic information about yourself.',
        description:
          "If you're looking to create multiple videos, select all that apply.",
        selectedValue: [],
      },
      {
        key: 'experiences',
        name: 'Experiences',
        status: 'empty',
        hero: 'Please add your current and past job experiences',
        validationProperties: [
          'experiences.0.company_name',
          'experiences.0.job_title',
        ],
        description:
          'We would like to assess your experience level. Help us by filling out your past work positions.',
        selectedValue: [],
      },
      {
        key: 'skills',
        name: 'Skills',
        status: 'empty',
        hero: 'Please add your skills',
        validationProperties: ['skills'],
        description:
          'We would like to assess your experience level. Help us by filling out your past work positions.',
        selectedValue: [],
        cards: [
          {
            inner_cards: [
              {
                key: 'pre_production',
                name: 'Pre-Production',
                description:
                  'Managing all the required steps prior to constructing the video.',
                inputHeader: 'Select Skills',
                checkboxes: [
                  {
                    value: 'voiceover',
                    description: 'Selecting a voiceover actor',
                  },
                  {
                    value: 'producer',
                    description: 'Coordinating with a producer',
                  },
                  { value: 'music', description: 'Choosing music' },
                  { value: 'text_script', description: 'Creating text script' },
                  {
                    value: 'stock_footage',
                    description: 'Choosing stock footage',
                  },
                  {
                    value: 'voiceover_script',
                    description: 'Choosing voiceover script',
                  },
                  { value: 'shot_list', description: 'Creating shot list' },
                  { value: 'storyboard', description: 'Creating storyboard' },
                ],
              },
              {
                key: 'production',
                name: 'Production',
                inputHeader: 'Select Production Types',
                description:
                  'Setting up, directing, and capturing live action content.',
                checkboxes: [
                  { value: 'drone', description: 'Drone' },
                  { value: 'brand', description: 'Brand' },
                  { value: 'commercial', description: 'Commercial' },
                  { value: 'event', description: 'Event' },
                  { value: 'product', description: 'Product' },
                  { value: 'testimonial', description: 'Testimonial' },
                  { value: 'other', description: 'Other' },
                ],
                other: true,
              },
              {
                key: 'animation',
                name: 'Animation / Motion Graphics',
                description: 'Making illustrations move gracefully on screen.',
                inputHeader: 'Select Skills',
                checkboxes: [
                  { value: 'chart_based', description: 'Chart Based' },
                  { value: 'line_art', description: 'Line art' },
                  { value: 'infographic', description: 'Infographic' },
                  { value: '3d_animation', description: '3D Animation' },
                  { value: 'whiteboard', description: 'Whiteboard' },
                  { value: 'kinetic_text', description: 'Kinetic text' },
                  { value: '2d_vector', description: '2D vector' },
                  { value: 'motion_tracking', description: 'Motion Tracking' },
                ],
              },
              {
                key: 'video_editing',
                name: 'Video Editing',
                description:
                  'Organizing the footage, stills, and audio to create a video.',
                inputHeader: 'Select Preferred Software',
                checkboxes: [
                  {
                    value: 'adobe_premiere',
                    description: 'Adobe Premiere',
                    icon: true,
                  },
                  {
                    value: 'final_cut',
                    description: 'Final Cut Pro',
                    icon: true,
                  },
                  {
                    value: 'after_effects',
                    description: 'After Effects',
                    icon: true,
                  },
                  {
                    value: 'apple_motion',
                    description: 'Apple Motion',
                    icon: true,
                  },
                  {
                    value: 'davinci_resolve',
                    description: 'Davinci Resolve',
                    icon: true,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: 'languages',
        name: 'Languages',
        status: 'empty',
        hero: 'What languages are you an expert in?',
        validationProperties: ['fluent_languages'],
        description: 'Select all that apply.',
        selectedValue: [],
        cards: [
          {
            isSpecial: true,
            value: 'languages',
          },
        ],
      },
      {
        key: 'project_type',
        name: 'Projects to Work',
        status: 'empty',
        validationProperties: ['project_type'],
        hero: 'What project types are you willing to work?',
        description:
          'At DreamItReel we have two distinct project types: Managed and Marketplace. Managed projects take the weight off your shoulders and provide a manager to communicate with the client while you get to focus on your work. Marketplace projects on the other had are fully managed by you. You get to bid on a variety of projects and have the opportunity to make more money! But you also assume responsibility for all aspects of the project.',
        selectedValue: [],
        cards: [
          {
            key: 'managed_service',
            text: 'Managed Service Only',
          },
          {
            key: 'marketplace',
            text: 'Marketplace Only',
          },
          {
            key: 'both',
            text: 'Both',
          },
        ],
      },
      {
        key: 'tagline',
        name: 'Yourself',
        status: 'empty',
        validationProperties: ['tagline', 'bio'],
        hero: 'Tell us about yourself',
        description:
          'The tagline and bio will be shown prominently on your profile so make it count!',
        selectedValue: [],
      },
    ],
  },
};

const overview = handleActions(
  {
    SET_NEXT_ONBOARDING_STEP: (state, action) => {
      const {
        payload: { formValues: user },
      } = action;

      const actualStep = state.currentStep;
      const currentOnboardingSteps =
        state.freelancer_onboarding.freelancerSteps;
      const cardIndex = currentOnboardingSteps.findIndex(
        elem => elem.key === actualStep,
      );

      return {
        ...state,
        currentStep: getNextFreelancerOnboardingStep(actualStep),
        freelancer_onboarding: {
          ...state.freelancer_onboarding,
          freelancerSteps: state.freelancer_onboarding.freelancerSteps.map(
            (step, i) =>
              i === cardIndex
                ? { ...step, status: getStepStatus(step, user) }
                : step,
          ),
        },
        isFetching: false,
      };
    },

    SET_PREV_ONBOARDING_STEP: state => ({
      ...state,
      currentStep: getPrevFreelancerOnboardingStep(state.currentStep),
    }),

    SET_CURRENT_ONBOARDING_STEP: (state, action) => ({
      ...state,
      currentStep: action.payload.currentStep,
    }),

    SET_FORM_VALUES: (state, action) => ({
      ...state,
      formValues: action.payload.values,
    }),

    GET_USER_SUCCESS: (state, action) => {
      const { data: user } = action.payload;
      const { current_step: currentStep } = user;

      return {
        ...state,
        currentStep: currentStep || state.currentStep,
        freelancer_onboarding: {
          ...state.freelancer_onboarding,
          freelancerSteps: state.freelancer_onboarding.freelancerSteps.map(
            step => ({
              ...step,
              status: getStepStatus(step, user),
            }),
          ),
        },
      };
    },

    ENABLE_NEXT_BUTTON: state => ({
      ...state,
      isNextEnabled: true,
    }),

    DISABLE_NEXT_BUTTON: state => ({
      ...state,
      isNextEnabled: false,
    }),
    AUTHENTICATION_LOGOUT: () => ({
      ...initialState,
    }),
  },
  initialState,
);

export const getStepStatus = (step, user) => {
  if (!step.validationProperties) {
    return step.status;
  }

  const isStepDone = !map(step.validationProperties, item =>
    isEmpty(get(user, item)),
  ).find(i => i === true);

  return isStepDone ? 'done' : 'empty';
};

export default overview;



// WEBPACK FOOTER //
// ./src/store/reducers/FreelancerOnboarding/index.js